/**
 * Cloudinary image transformation utilities
 */

import { getCloudinaryConfig } from './cloudinary-config-parser';

// Get cloud name from existing config
const getCloudName = (): string => {
  try {
    const config = getCloudinaryConfig();
    return config.cloudName;
  } catch (error) {
    console.warn('Cloudinary config not found, using fallback');
    return import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || 'demo';
  }
};

const CLOUDINARY_BASE_URL = `https://res.cloudinary.com/${getCloudName()}`;

/**
 * Transform an image URL to a Cloudinary thumbnail
 * @param imageUrl - Original image URL (can be Cloudinary or external)
 * @param width - Thumbnail width (default: 200)
 * @param height - Thumbnail height (default: 160)
 * @param quality - Image quality (default: auto)
 * @param format - Image format (default: auto)
 * @returns Cloudinary thumbnail URL
 */
export function getCloudinaryThumbnail(
  imageUrl: string,
  width: number = 200,
  height: number = 160,
  quality: string = 'auto',
  format: string = 'auto'
): string {
  if (!imageUrl) {
    return `${CLOUDINARY_BASE_URL}/image/upload/w_${width},h_${height},c_fill,f_${format},q_${quality}/placeholder_image.jpg`;
  }

  // If it's already a Cloudinary URL, extract the public ID and rebuild with transformations
  if (imageUrl.includes('res.cloudinary.com')) {
    // Extract the path after /upload/ including version
    const uploadMatch = imageUrl.match(/\/upload\/(.+)$/);
    if (uploadMatch) {
      const pathAfterUpload = uploadMatch[1];
      // Insert transformations before the path
      return `${CLOUDINARY_BASE_URL}/image/upload/w_${width},h_${height},c_fill,f_${format},q_${quality}/${pathAfterUpload}`;
    }
  }

  // For external URLs, use fetch transformation
  const encodedUrl = encodeURIComponent(imageUrl);
  return `${CLOUDINARY_BASE_URL}/image/fetch/w_${width},h_${height},c_fill,f_${format},q_${quality}/${encodedUrl}`;
}

/**
 * Get a product thumbnail URL optimized for product cards
 * @param imageUrl - Original product image URL
 * @returns Optimized thumbnail URL (200x160)
 */
export function getProductThumbnail(imageUrl: string): string {
  return getCloudinaryThumbnail(imageUrl, 200, 160, 'auto', 'auto');
}

/**
 * Get multiple thumbnail sizes for responsive images
 * @param imageUrl - Original image URL
 * @returns Object with different thumbnail sizes
 */
export function getResponsiveThumbnails(imageUrl: string) {
  return {
    small: getCloudinaryThumbnail(imageUrl, 150, 120),
    medium: getCloudinaryThumbnail(imageUrl, 200, 160),
    large: getCloudinaryThumbnail(imageUrl, 300, 240),
  };
}

/**
 * Preload Cloudinary transformations for better performance
 * @param imageUrls - Array of image URLs to preload
 */
export function preloadProductThumbnails(imageUrls: string[]): void {
  imageUrls.forEach((url) => {
    if (url) {
      const thumbnailUrl = getProductThumbnail(url);
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = thumbnailUrl;
      document.head.appendChild(link);
    }
  });
} 